import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../../../utils/api';

const CreateTermsDrawer = ({ isOpen, onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    section_title: '',
    section_html: '',
    is_active: true,
  });
  const [error, setError] = useState('');
  const quillRef = useRef(null);

  // Modules para ReactQuill sin activar listas automáticas por `- ` o `* `
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, false] }],
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: ['', 'center', 'right', 'justify'] }],
      [{ color: [] }],
      ['clean'],
    ],
    clipboard: {
      matchVisual: false,
    },
  };

  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (editor) {
      editor.root.addEventListener('keydown', handlePreventAutoList);
    }
    return () => {
      editor?.root.removeEventListener('keydown', handlePreventAutoList);
    };
  }, []);

  const handlePreventAutoList = (e) => {
    if (e.key === ' ' && e.target?.textContent?.trim() === '-') {
      e.preventDefault();
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleHtmlChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      section_html: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.section_title || !formData.section_html) {
      setError('Required fields: Title and Content');
      return;
    }
    setError('');
    try {
      await api.post('/api/terminos', {
        section_title: formData.section_title,
        section_html: formData.section_html,
        is_active: formData.is_active ? 1 : 0,
      });
      if (onCreated) onCreated();
      onClose();
      setFormData({ section_title: '', section_html: '', is_active: true });
    } catch (err) {
      setError(err.response?.data?.message || 'Error creating term');
      console.error('Error creating term:', err);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-y-0 right-0 max-w-full flex sm:w-full sm:max-w-[700px]">
        <DialogPanel className="w-full bg-white shadow-2xl rounded-l-lg flex flex-col h-screen sm:rounded-r-lg">
          <div className="p-6 overflow-y-auto flex-1 space-y-6">
            <div>
              <DialogTitle className="text-2xl font-bold text-gray-900">Create New Term</DialogTitle>
              <p className="mt-2 text-sm text-gray-600">Add a new term to the system. Fields marked with * are required.</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <div className="flex items-center justify-between">
                  <div>
                    <label htmlFor="is_active" className="block text-sm font-medium text-gray-700">Term Status</label>
                    <p className="text-xs text-gray-500 mt-1">Toggle to activate or deactivate the term.</p>
                  </div>
                  <div className="flex items-center">
                    <input
                      id="is_active"
                      name="is_active"
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={handleChange}
                      className="sr-only"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, is_active: !prev.is_active }))}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${formData.is_active ? 'bg-blue-600' : 'bg-gray-200'}`}
                      role="switch"
                      aria-checked={formData.is_active}
                      aria-labelledby="is_active"
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${formData.is_active ? 'translate-x-5' : 'translate-x-0'}`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="section_title" className="block text-sm font-medium text-gray-700">Title *</label>
                <p className="text-xs text-gray-500 mt-1">The title of the term section.</p>
                <input
                  id="section_title"
                  type="text"
                  name="section_title"
                  value={formData.section_title}
                  onChange={handleChange}
                  placeholder="Term title"
                  className="mt-1 block w-full border border-gray-200 rounded-md shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  required
                  aria-required="true"
                />
              </div>

              <div>
                <label htmlFor="section_html" className="block text-sm font-medium text-gray-700">Content *</label>
                <p className="text-xs text-gray-500 mt-1">The HTML content of the term section.</p>
                <div className="mt-1">
                  <ReactQuill
                    ref={quillRef}
                    id="section_html"
                    theme="snow"
                    value={formData.section_html}
                    onChange={handleHtmlChange}
                    modules={quillModules}
                    className="border border-gray-200 rounded-md shadow-sm focus-within:ring-blue-500 focus-within:border-blue-500 sm:text-sm"
                    placeholder="Term content"
                  />
                </div>
              </div>

              {error && <p className="text-red-500 text-sm">{error}</p>}
            </form>
          </div>
          <div className="bg-gray-50 border-t px-6 py-4 flex justify-between gap-4 sticky bottom-0">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              Close
            </button>
            <button
              onClick={handleSubmit}
              className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800"
            >
              Create
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default CreateTermsDrawer;
